// What the Workspaces page and Settings say about the relation between
// workspaces (02-DESIGN/12-workspace-hierarchy/01-the-relation.md § the
// Workspaces page): the words — a child is *a workspace you manage*, the
// parent *the workspace that manages this one*, a solo workspace *not
// managed* — the limit read before a form is submitted, the transfer
// targets, and one sentence per refusal of the parent's verbs.

import type { Child, Limits, Tenant } from "../../wire/auth.ts";
import { RelationCode } from "../../wire/auth.ts";

/** managesWorkspaces reports whether the workspace may manage workspaces, from its own values. */
export function managesWorkspaces(t: Tenant | undefined): boolean {
  return t?.limits?.manages_workspaces === true;
}

/**
 * roomForAnother reports whether one more workspace fits beneath the caller,
 * read from its own values before the form is submitted; the service checks
 * again at its root and at every ancestor.
 */
export function roomForAnother(l: Limits | undefined): boolean {
  if (l === undefined) {
    return true;
  }
  return l.workspaces < 0 || l.beneath < l.workspaces;
}

/** childState is a row's state: *no blueprint yet* while solo, *managed* once a blueprint exists. */
export function childState(c: Child): string {
  return c.managed ? "managed" : "no blueprint yet";
}

/** childName is how a row names a child: its display name, or its slug. */
export function childName(c: { slug: string; name?: string }): string {
  return c.name !== undefined && c.name !== "" ? c.name : c.slug;
}

/** A place a child may be handed to. */
export interface Target {
  slug: string;
  label: string;
}

/**
 * transferTargets are the places the page offers a child to: the caller's
 * other children — a hand down — and the caller's own parent — a hand up.
 * The service refuses anything outside the caller's reach.
 */
export function transferTargets(
  child: string,
  children: readonly Child[],
  self: Tenant | undefined,
): Target[] {
  const out: Target[] = children
    .filter((c) => c.slug !== child)
    .map((c) => ({ slug: c.slug, label: `${childName(c)} (a workspace you manage)` }));
  if (self?.parent !== undefined) {
    out.push({
      slug: self.parent.slug,
      label: `${childName(self.parent)} (the workspace that manages this one)`,
    });
  }
  return out;
}

/** managedBy is what Settings says of the relation; nothing for a root. */
export function managedBy(t: Tenant | undefined): string | undefined {
  if (t?.parent === undefined) {
    // A root the operator keeps as code is managed by the platform.
    return t?.managed === true ? "managed by the platform" : undefined;
  }
  const by = `managed by ${childName(t.parent)}`;
  return t.managed === true ? by : `${by} · no blueprint yet`;
}

/** relationRefusal is one sentence for a refusal of the parent's verbs, or nothing for another code. */
export function relationRefusal(err: unknown): string | undefined {
  const code = typeof err === "object" && err !== null && "code" in err ? err.code : undefined;
  switch (code) {
    case RelationCode.limit:
      return "There is no room for another workspace: the limit on workspaces beneath this one is reached.";
    case RelationCode.notAllowed:
      return "This workspace may not manage workspaces.";
    case RelationCode.slugTaken:
      return "That address is another workspace's. Choose another.";
    case RelationCode.notFound:
      return "That is not a workspace you manage.";
    case RelationCode.cycle:
      return "A workspace cannot be handed to itself or to a workspace beneath it.";
    case RelationCode.outsideReach:
      return "That workspace is out of reach: hand a workspace only to one you manage, or to the workspace that manages this one.";
    case RelationCode.singleRoot:
      return "Every workspace on this install descends from its root; a workspace is not detached here.";
    case RelationCode.hasChildren:
      return "This workspace has workspaces beneath it; transfer or detach them first.";
  }
  return undefined;
}
